import { type ReactNode, useState, useEffect } from 'react';
import { Link } from '../../components/Link';
import { PublicFooter } from '../../components/PublicFooter';
import { PublicHeader } from '../../components/PublicHeader';
import { Button } from '../../components/ui';
import { Seo, breadcrumbFor } from '../../lib/seo';
import { useFeatures } from '../../lib/useFeatures';
import { useBillingMode } from '../../lib/useBillingMode';

function Code({ children }: { children: ReactNode }) {
  return (
    <pre className="my-3 overflow-auto rounded-[10px] border border-bordersoft bg-panel2 p-4 font-mono text-[12.5px] leading-relaxed text-muted">
      {children}
    </pre>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24 border-t border-bordersoft pt-10">
      <h2 className="font-display text-2xl font-bold tracking-tight">{title}</h2>
      <div className="mt-3 text-[14px] leading-relaxed text-muted [&_a]:text-accent [&_code]:rounded [&_code]:bg-panel2 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[12.5px] [&_code]:text-txt">
        {children}
      </div>
    </section>
  );
}

const TOC = [
  ['intro', 'Introduction'],
  ['auth', 'Authentication'],
  ['quickstart', 'Quickstart'],
  ['concepts', 'Core concepts'],
  ['endpoints', 'Endpoints'],
  ['bulk', 'Async bulk ingest'],
  ['reviews', 'Reviews & decisions'],
  ['workspace-features', 'Workspace features'],
  ['webhooks', 'Webhooks'],
  ['alerts', 'Alerts (Slack / email / Intercom)'],
  ['errors', 'Errors & limits'],
];

export function DocsPage() {
  const { feature } = useFeatures();
  // Wallet vs legacy framing drives the per-feature pricing prose + the addon-tier
  // paragraph at the bottom of Workspace features. Falls back to legacy when the
  // hook hasn't resolved (the public hero loads first; this is a non-blocking
  // optimisation). When wallet is ON: the same numbers come from the live cost
  // table so a super-admin tune in Admin → Credits propagates here too.
  const walletEnabled = useBillingMode().unifiedWalletEnabled;
  // Auto-derive the API base URL from the origin the docs are served from — the web
  // and the API are served behind the same host (nginx proxies /api → api service), so
  // this is correct out-of-the-box on every deployment. Build-time `VITE_PUBLIC_API_URL`
  // (e.g. a CDN vanity domain) overrides it when set.
  const buildtimeApiUrl = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env
    ?.VITE_PUBLIC_API_URL;
  // SSR-safe: the server prerender and the client's FIRST (hydration) render must produce
  // identical HTML, so both start from the build-time URL (or the relative `/api`). Only
  // AFTER hydration does the client swap in the live `window.location.origin` — a state
  // update, not a hydration-time value — avoiding a React #418 text mismatch on the curl
  // examples. When VITE_PUBLIC_API_URL is set, the value is stable and no swap happens.
  const [baseUrl, setBaseUrl] = useState(() =>
    buildtimeApiUrl ? buildtimeApiUrl.replace(/\/$/, '') : '/api',
  );
  useEffect(() => {
    if (!buildtimeApiUrl && typeof window !== 'undefined') {
      setBaseUrl(`${window.location.origin}/api`);
    }
  }, [buildtimeApiUrl]);
  const curlExample = `curl -X POST ${baseUrl}/v1/emails \\
  -H "X-API-Key: $SIEVE_API_KEY" \\
  -H "content-type: application/json" \\
  -d '{ "emails": ["sarah.chen@gmail.com", "anita00x00@gmail.com"] }'`;

  return (
    <>
      <Seo
        title="API docs"
        description="Integrate SieveGuard in minutes. Authentication, quickstart, endpoints, async bulk ingest, webhooks, errors."
        path="/docs"
        jsonLd={[breadcrumbFor([{ name: 'Home', path: '/' }, { name: 'API docs', path: '/docs' }])]}
      />
    <div className="mx-auto max-w-[1210px] px-6 pb-24">
      <PublicHeader />

      {/* mt-14 matches the top breathing room every other public page gives its first
          section (Pricing / Features / Landing all use py-14 on their hero). Without it
          the docs H1 sits flush under the header on large resolutions. */}
      <div className="mt-14 grid gap-10 md:grid-cols-[200px_1fr]">
        <nav className="hidden md:block">
          <div className="sticky top-6 flex flex-col gap-1.5">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.16em] text-faint">
              Developer docs
            </div>
            {TOC.map(([id, label]) => (
              <a
                key={id}
                href={`#${id}`}
                className="text-[13.5px] text-muted transition hover:text-txt"
              >
                {label}
              </a>
            ))}
          </div>
        </nav>

        <div className="min-w-0">
          <section id="intro" className="scroll-mt-24">
            <h1 className="font-display text-4xl font-extrabold tracking-tight">
              SieveGuard API <span className="text-accent">docs</span>
            </h1>
            <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-muted">
              Integrate email-integrity screening in minutes. Submit addresses, get back a risk
              score, an action (<code>allow</code> / <code>verify</code> / <code>review</code>), a
              deliverability advisory, and a final disposition — over a simple REST API.
            </p>
            <p className="mt-4 text-[13.5px] text-faint">
              Base URL: <code className="text-txt">{baseUrl}</code> · all endpoints below are
              under <code className="text-txt">/v1</code>. The full machine reference (try-it-out)
              lives at{' '}
              <a className="text-accent" href="/api/docs" target="_blank" rel="noreferrer">
                /api/docs
              </a>
              .
            </p>
          </section>

          <Section id="auth" title="Authentication">
            <p>
              Create an API key in the backoffice under <b>API keys</b> (shown once — store it
              securely). Authenticate every request with either header:
            </p>
            <Code>{`X-API-Key: sk_live_xxx.your_secret
# or
Authorization: Bearer sk_live_xxx.your_secret`}</Code>
            <p>
              Each key is scoped to your workspace and rate-limited (HTTP <code>429</code> when
              exceeded). All data you read or write is automatically isolated to your tenant.
            </p>
          </Section>

          <Section id="quickstart" title="Quickstart">
            <p>Screen a batch of addresses:</p>
            <Code>{curlExample}</Code>
            <p>Response — one result per address:</p>
            <Code>{`[
  {
    "id": "…", "email": "sarah.chen@gmail.com", "valid": true,
    "canonical": "sarahchen@gmail.com",
    "score": 0, "action": "allow", "disposition": "accepted",
    "reasons": [], "nearCount": 0, "cluster": null,
    "verification": { "status": "deliverable", "exists": true, "disposable": false }
  },
  {
    "id": "…", "email": "anita00x00@gmail.com", "valid": true,
    "score": 70, "action": "review", "disposition": "pending",
    "reasons": ["structural", "near_duplicates"],
    "cluster": { "id": "…", "size": 4 },
    "verification": { "status": "deliverable", "exists": true, "disposable": false }
  }
]`}</Code>
          </Section>

          <Section id="concepts" title="Core concepts">
            <p>
              <b>action</b> — the automated risk tier: <code>allow</code> (clean),{' '}
              <code>verify</code> (soft flag), <code>review</code> (looks bot-enumerated /
              multi-accounting).
            </p>
            <p className="mt-2">
              <b>disposition</b> — the final outcome you act on: <code>accepted</code>,{' '}
              <code>pending</code> (awaiting a human decision), or <code>declined</code>.
            </p>
            <p className="mt-2">
              <b>review mode</b> decides how <code>review</code>-tier emails resolve:
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>
                <code>manual</code> — review-tier → <code>pending</code> (you accept/decline later).
              </li>
              <li>
                <code>automated</code> — the engine decides: review-tier → <code>declined</code>,
                otherwise <code>accepted</code>.
              </li>
            </ul>
            <p className="mt-2">
              Resolution precedence: <b>per-request <code>reviewMode</code></b> →{' '}
              <b>per-list default</b> → <b>workspace default</b>. Override per call:
            </p>
            <Code>{`{ "emails": [...], "listId": "…", "reviewMode": "automated" }`}</Code>
            <p>
              <b>lists/filters</b> — scope detection to a cohort so duplicates are only compared
              within that list. Pass <code>listId</code>; each list has its own tuning
              (fuzzy radius, cluster threshold) and review-mode default.
            </p>
          </Section>

          <Section id="endpoints" title="Endpoints">
            <Code>{`POST   /v1/emails              Submit one or a batch (≤ 1000). Body:
                               { emails: string[], listId?, reviewMode? }
GET    /v1/emails              List stored records (?listId, ?action, ?limit, ?offset)
GET    /v1/emails/:id          Full detail for one record
POST   /v1/emails/:id/decision Accept/decline a pending record  { decision, note? }
GET    /v1/reviews             The pending-review queue
GET    /v1/clusters            Detected near-duplicate clusters (?listId)
GET    /v1/clusters/:id        Cluster detail + first 200 members
GET    /v1/analytics           Aggregate metrics (?listId)
POST   /v1/emails/bulk         Async ingest (≤ 25k) → { jobId }
GET    /v1/jobs/:id            Bulk job status

GET    /v1/lists               List your lists (newest first)
POST   /v1/lists               Create a list. Body:
                               { name, description?, maxDistance?,
                                 clusterThreshold?, reviewMode?,
                                 reflagPriorOnDuplicate? }
GET    /v1/lists/:id           Get a list + its current settings
PATCH  /v1/lists/:id           Update a list (any subset of fields)
DELETE /v1/lists/:id           Delete a list (204; records detached)
GET    /v1/lists/:id/records   Records on this list (same filters as
                               GET /v1/emails — the listId is pinned
                               from the path)

GET    /v1/warmup-inbox/access            Slot quota + add-on / plan source
GET    /v1/warmup-inbox/inboxes           List your warming mailboxes
POST   /v1/warmup-inbox/inboxes           Add a new SMTP mailbox. Body:
                                          { email, senderFirst, senderLast,
                                            timezone?, smtp:{username,password,
                                            host,port,tls}, imap:{...} }
                                          (Google + Office mailboxes are
                                          portal-only — interactive OAuth.)
GET    /v1/warmup-inbox/inboxes/:id          Local cache snapshot
GET    /v1/warmup-inbox/inboxes/:id/metrics  Live placement metrics from WUI
GET    /v1/warmup-inbox/inboxes/:id/details  Live settings + health
PATCH  /v1/warmup-inbox/inboxes/:id           Update mailbox settings
POST   /v1/warmup-inbox/inboxes/:id/start     Resume warming
POST   /v1/warmup-inbox/inboxes/:id/pause     Pause (slot reserved)
DELETE /v1/warmup-inbox/inboxes/:id           Delete (slot freed; 204)

GET    /v1/billing/summary           Plan + add-ons + estimated monthly total
GET    /v1/billing/subscription      Current subscription state
GET    /v1/billing/addons            Active add-on tiers + monthly cost
GET    /v1/billing/usage             Month-to-date usage by feature
GET    /v1/billing/invoices          Recent invoices (?limit, default 12)
GET    /v1/billing/invoices/:id      One invoice`}</Code>
            <p className="mt-3 text-[13px] text-faint">
              These are the API-key-authenticated <code>/v1</code> endpoints — anything you can
              hit from your integration code. Tenant add-ons (Phone Validation, Inbox Placement,
              Domain Health, Blacklist Checks) are configured + consumed in the workspace
              backoffice — see{' '}
              <a className="text-accent" href="#workspace-features">Workspace features</a> below.
            </p>
          </Section>

          <Section id="bulk" title="Async bulk ingest">
            <p>
              For large uploads, enqueue a job and poll for completion. Detection (clustering
              / scoring) and mailbox-level verification both run server-side; addresses are
              shipped to the mailbox-level verifier in one batch instead of one HTTP call per
              address (faster, identical credit cost). Webhooks +{' '}
              <a className="text-accent" href="#webhooks">notification channels</a> fire on results.
            </p>
            <Code>{`POST /v1/emails/bulk  { "emails": [ ...up to 25,000... ], "listId": "…" }
→ 202 { "jobId": "42", "count": 25000, "status": "queued" }

GET /v1/jobs/42
→ { "id": "42", "state": "completed", "progress": 100,
    "result": { "processed": 25000, "review": 318 } }`}</Code>
            <p>
              The endpoint pre-validates against your remaining{' '}
              {walletEnabled
                ? 'SieveGuard-Credits wallet'
                : 'monthly email / credit allowance'}{' '}
              and returns <code>402</code> if the upload alone would exceed it — split the
              file, {walletEnabled ? 'top up with a credit pack' : 'upgrade your plan'}, or
              upgrade.
            </p>
          </Section>

          <Section id="reviews" title="Reviews & decisions">
            <p>
              In <code>manual</code> mode, review-tier emails are <code>pending</code>. Fetch the
              queue and resolve each — your decision can also fire a webhook (below):
            </p>
            <Code>{`GET  /v1/reviews
POST /v1/emails/{id}/decision   { "decision": "declined", "note": "bot pattern" }`}</Code>
            <p>
              Deciding an already-resolved record returns <code>409</code>.
            </p>
          </Section>

          <Section id="workspace-features" title="Workspace features">
            <p>
              Three of the four add-ons ship public <code>/v1</code> API endpoints (same API
              key, same rate limits as the screening API). {walletEnabled ? (
                <>
                  Every billable action draws from your monthly{' '}
                  <b>SieveGuard-Credits wallet</b> at the per-action cost listed below — same
                  cost table the operator tunes in Admin → Credits, exposed to the public via{' '}
                  <code>GET /platform/billing-mode</code>. Top up mid-month with a one-shot
                  credit pack from <b>Portal → Billing → Credit packs</b>.
                </>
              ) : (
                <>
                  Activate the add-on from <b>Profile → Add-ons</b> (slider tiers,
                  Stripe-billed) and call the endpoints programmatically. Bundle inclusions
                  per Pro tier are shown on the{' '}
                  <a className="text-accent" href="/pricing/">pricing</a> page.
                </>
              )}
            </p>
            {feature('domain_health') && (
              <>
                <h3 className="mt-5 text-base font-semibold text-txt">Domain Health</h3>
                <p>
                  SieveGuard SPF / DKIM / DMARC / MX checker, plus an optional blacklist pass (when the
                  Blacklist Checks add-on is on). Every domain gets a 0–100 score; results are cached
                  for 24h. Repeat lookups within the TTL don't consume credits.
                </p>
                <Code>{`GET   /v1/domain-health/access            Effective access (plan + add-on gates)
GET   /v1/domain-health                   List cached snapshots
GET   /v1/domain-health/bulk?domains=a,b  Bulk lookup (cached only, no refresh)
GET   /v1/domain-health/:domain           Auto-refresh on expiry
POST  /v1/domain-health/:domain/refresh   Force-refresh from DNS`}</Code>
              </>
            )}

            {feature('inbox_placement') && (
              <>
                <h3 className="mt-5 text-base font-semibold text-txt">Inbox Placement</h3>
                <p>
                  Sends a tracking-coded test from your production sender to a set of seed addresses
                  and reports where it lands (Inbox / Promotions / Spam / Missing) per ESP. {walletEnabled ? (
                    <>
                      Each test consumes a fixed number of SieveGuard Credits from your wallet
                      (live cost from <code>GET /platform/billing-mode</code> → <code>creditCosts.inboxPlacement</code>;
                      auto-refunded if no delivery is detected).
                    </>
                  ) : (
                    <>
                      100 platform credits per test (refunded if no delivery is detected).
                    </>
                  )} 30/60/90-day sender-reputation trend is derived from completed tests.
                </p>
                <Code>{`GET   /v1/inbox-placement/access       Effective access + quota
GET   /v1/inbox-placement              List tests
GET   /v1/inbox-placement/trend        30/60/90-day reputation trend
POST  /v1/inbox-placement              Create test → { code, seedEmails }
GET   /v1/inbox-placement/:id          Poll for results
POST  /v1/inbox-placement/:id/refresh  Force-refresh from provider`}</Code>
              </>
            )}

            {feature('phone_validation') && (
              <>
                <h3 className="mt-5 text-base font-semibold text-txt">Phone Validation</h3>
                <p>
                  Phone-number validation — country, carrier, line type
                  (mobile / VoIP / toll-free / fixed). Cache-first: repeat lookups of the same E.164
                  are free. Bulk uploads explode into the cache so subsequent single-number lookups
                  are also free.
                </p>
                <Code>{`GET   /v1/phone-validation/access      Effective access + quota
GET   /v1/phone-validation             Recent cached lookups
POST  /v1/phone-validation/verify      Single number → { e164, phoneType, carrier, ... }
POST  /v1/phone-validation/bulk        Upload CSV → { id, status }
GET   /v1/phone-validation/bulk        List bulk jobs
GET   /v1/phone-validation/bulk/:id    Job status + per-row counts`}</Code>
              </>
            )}

            {feature('blacklist') && (
              <>
                <h3 className="mt-5 text-base font-semibold text-txt">Blacklist Checks</h3>
                <p>
                  Blacklist aggregation (50+ industry blacklists) on every domain / IP you ingest. {walletEnabled ? (
                    <>
                      Each check consumes a fixed number of SieveGuard Credits from your wallet
                      (live cost from <code>GET /platform/billing-mode</code> → <code>creditCosts.blacklist</code>).
                      The credit weight is a fixed worst-case figure — cache hits within the TTL
                      don't re-charge.
                    </>
                  ) : (
                    <>Each check costs 10 platform credits.</>
                  )} There&apos;s no separate endpoint — results land on the matching{' '}
                  <code>/v1/domain-health/:domain</code> snapshot alongside the auth posture (the BL
                  pass runs automatically when the add-on is active and the cache row is refreshed).
                </p>
              </>
            )}

            <p className="mt-4 text-[13px] text-faint">
              {walletEnabled ? (
                <>
                  Top up your wallet mid-month with a one-shot credit pack:{' '}
                  <code>POST /billing/credit-packs/checkout</code> with{' '}
                  <code>{'{ packId }'}</code> returns a Stripe Checkout URL the tenant
                  completes; the webhook handler credits{' '}
                  <code>Tenant.purchasedCredits</code> on success.{' '}
                  <code>GET /billing/credit-packs</code> lists the active catalog.
                  Wallet balance + per-feature monthly spend:{' '}
                  <code>GET /credit/wallet</code>. Full OpenAPI at{' '}
                  <a className="text-accent" href="/api/docs" target="_blank" rel="noreferrer">/api/docs</a>.
                </>
              ) : (
                <>
                  Add-on tier changes are pro-rated by Stripe on every slider move:{' '}
                  <code>PATCH /billing/addons</code> with{' '}
                  <code>{'{ addon, quota }'}</code> (JWT-only, since it&apos;s a billing operation).
                  Tier ladders are exposed at <code>GET /billing/addons/tiers</code>. Both also live
                  in the full OpenAPI reference at{' '}
                  <a className="text-accent" href="/api/docs" target="_blank" rel="noreferrer">/api/docs</a>.
                </>
              )}
            </p>
          </Section>

          <Section id="webhooks" title="Webhooks">
            <p>
              Configure endpoints in the backoffice under <b>Webhooks</b> and subscribe per event:
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>
                <code>emails.scored</code> — fired once per ingest request with the results.
              </li>
              <li>
                <code>email.reviewed</code> — fired when a pending record is accepted/declined.
              </li>
              <li>
                <code>email.duplicate_detected</code> — fired when a newly-ingested address
                canonicalises to a mailbox that already has prior records on the same list (Gmail
                alias gaming, +tag stripping, etc.). Lets your system react retrospectively —
                revoke access on the earlier accepted record, flag for fraud review, etc.
              </li>
              <li>
                <code>inbox_placement.test_completed</code> — fired when an Inbox Placement
                test transitions from <code>running</code> → <code>complete</code>. Replaces
                polling <code>GET /v1/inbox-placement/:id</code> — subscribe to this and you
                only call the GET endpoint once, to fetch the recipient-level detail you
                actually want.
              </li>
              <li>
                <code>phone_validation.bulk_completed</code> — fired when an async bulk-CSV
                phone job hits the <code>completed</code> stage. Replaces polling{' '}
                <code>GET /v1/phone-validation/bulk/:id</code> — payload carries the row
                totals so most clients won't even need a follow-up GET.
              </li>
              <li>
                <code>warmup_inbox.added</code> · <code>.paused</code> ·{' '}
                <code>.resumed</code> · <code>.deleted</code> — mailbox lifecycle events.
                Fire from both API calls and portal actions, so an automation can stay in
                sync with whatever happens to a warming mailbox. Payload carries the
                inbox id, email, current status, plan tier, and last-known reputation
                score.
              </li>
              <li>
                <code>billing.addon_tier_changed</code> — fired whenever an add-on slider
                moves (in the portal or via an upcoming write endpoint). Payload carries
                the add-on kind, previous quota, new quota, and the Stripe subscription
                item id.
              </li>
              <li>
                <code>billing.plan_changed</code> — main plan or billing interval changed.
                Fires from the Stripe webhook so it covers admin-portal cancels too, not
                just portal-driven upgrades. Payload: previous + new plan ids/names and
                intervals.
              </li>
              <li>
                <code>billing.invoice_paid</code> · <code>billing.invoice_failed</code>{' '}
                — mirrors Stripe's <code>invoice.payment_succeeded</code> /{' '}
                <code>invoice.payment_failed</code> and lets your finance pipeline react
                without subscribing to Stripe's webhook directly. Payload includes the
                invoice id, amount, currency, hosted-invoice URL, and (on failure) the
                decline reason.
              </li>
              <li>
                <code>domain.blacklisted</code> — a previously-clean domain just landed on
                a blacklist. Fires only on the <em>transition</em> (clean → flagged), so
                repeated checks that find the domain still listed stay silent. Payload
                carries the domain, hit count, the listings array, and the current
                composite score.
              </li>
              <li>
                <code>domain_health.score_changed</code> — composite SPF / DKIM / DMARC /
                MX score dropped a band (75 → 50, 50 → 25). Upward recoveries don't
                fire — automation usually cares about regressions, not improvements.
                Payload: previous + new score, previous + new band, checked-at.
              </li>
              <li>
                <code>billing.invoice_upcoming</code> — Stripe's ~72h heads-up before an
                invoice draws. Requires the <code>invoice.upcoming</code> event to be
                enabled on the Stripe dashboard webhook config; without that, this
                event never fires. Payload: invoice id, amount due, currency, due
                date, hosted-invoice URL.
              </li>
              <li>
                <code>warmup_inbox.warmed_up</code> — reputation crossed the warmed-up
                threshold (default 80) for the first time. Fires exactly once per
                inbox lifecycle. Useful for "ready to start outreach" automation.
              </li>
              <li>
                <code>warmup_inbox.reputation_dropped</code> — reputation regressed by
                ≥ 10 points vs the prior sync. Includes the delta and the previous
                score so your automation can size the response.
              </li>
              <li>
                <code>warmup_inbox.daily_report</code> — per-mailbox snapshot fired by
                each sync. Includes status, reputation, ESP breakdown, warmedUpAt.
                Webhook only — too high-volume for human-readable alerting.
              </li>
              <li>
                <code>quota.threshold_crossed</code> — monthly usage crossed 80% or
                100% of allowance on a {walletEnabled
                  ? 'metered feature OR the wallet itself (when ≥80% of the monthly credit allowance is spent)'
                  : 'feature (currently Inbox Placement / Phone Validation / Blacklist)'}.
                At most once per ({walletEnabled ? 'scope' : 'feature'}, month, threshold)
                combo. Payload: feature, used, allowance, usedPct, threshold.
              </li>
            </ul>
            <p className="mt-2">
              Each delivery is a <code>POST</code> with an HMAC-SHA256 signature over{' '}
              <code>{'{timestamp}.{rawBody}'}</code> using your endpoint secret. Deliveries retry
              with backoff. Verify authenticity:
            </p>
            <Code>{`// headers: X-Sieve-Event, X-Sieve-Delivery, X-Sieve-Signature: t=<ts>,v1=<hmac>
import { createHmac } from 'node:crypto';

function verify(rawBody, header, secret) {
  const [t, v1] = header.split(',').map((p) => p.split('=')[1]);
  const expected = createHmac('sha256', secret).update(\`\${t}.\${rawBody}\`).digest('hex');
  return expected === v1;
}`}</Code>
            <p>Example payloads — one per event type:</p>
            <Code>{`// emails.scored — fired once per ingest request
{ "id": "<delivery-id>", "event": "emails.scored", "createdAt": "…",
  "data": { "listId": "…",
            "records": [ { "id": "…", "email": "…", "action": "review",
                           "disposition": "pending", "score": 70, "reasons": [...] } ] } }

// email.reviewed — fired on accept/decline of a pending record
{ "id": "<delivery-id>", "event": "email.reviewed", "createdAt": "…",
  "data": { "record": { "id": "…", "disposition": "declined", ... },
            "decision": "declined", "reviewedBy": "<user-id>" } }

// email.duplicate_detected — fired when a new address folds onto a prior canonical
{ "id": "<delivery-id>", "event": "email.duplicate_detected", "createdAt": "…",
  "data": { "newRecordId": "…", "canonical": "sarahchen@gmail.com",
            "priorRecordIds": ["…", "…"], "listId": "…" } }

// inbox_placement.test_completed — fired when an async test transitions to complete
{ "id": "<delivery-id>", "event": "inbox_placement.test_completed", "createdAt": "…",
  "data": { "testId": "…", "name": "Acme launch", "status": "complete",
            "senderDomain": "acme.com", "detectedSender": "outreach@acme.com",
            "summary": { "inbox": 17, "promotions": 4, "spam": 2,
                         "waiting": 0, "missing": 1 },
            "completedAt": "…" } }

// phone_validation.bulk_completed — fired when an async bulk job lands completed
{ "id": "<delivery-id>", "event": "phone_validation.bulk_completed", "createdAt": "…",
  "data": { "jobId": "…", "name": "Q3 list", "status": "completed",
            "rowsTotal": 12000, "rowsBillable": 11820,
            "rowsValid": 9874, "rowsInvalid": 1846, "rowsSyntaxError": 100,
            "completedAt": "…" } }`}</Code>
          </Section>

          <Section id="alerts" title="Alerts (Slack / email / Intercom)">
            <p>
              Webhooks are HTTP-out for server-to-server integration. <b>Alerts</b> are the
              same events delivered to a human channel — Slack, an email address, or Intercom.
              Tenant owners configure channels under <b>Notifications</b> in the portal and
              pick which events should fire each one.
            </p>
            <p className="mt-2">Channels can subscribe to these events:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>
                <code>emails.scored</code> — same trigger as the webhook event. Channels
                additionally apply a per-record disposition filter (any of{' '}
                <code>accepted</code> / <code>pending</code> / <code>declined</code>).
              </li>
              <li>
                <code>email.reviewed</code> — fires alongside the webhook event when a
                pending record is decided.
              </li>
              <li>
                <code>inbox_placement.test_completed</code> — "your test is done" with the
                inbox / promotions / spam count baked into the alert summary.
              </li>
              <li>
                <code>inbox_placement.reputation_regressed</code> — 30-day Inbox Placement
                trend dropped 5+ points vs the prior half. The killer "your deliverability
                is sliding" alert.
              </li>
              <li>
                <code>phone_validation.bulk_completed</code> — bulk-CSV job finished, with
                a short summary of the row totals in the channel message.
              </li>
              <li>
                <code>verifier_credit.threshold_crossed</code> — monthly verifier-credit
                usage crossed the alert threshold. Comes with an upgrade CTA.
              </li>
              <li>
                <code>billing.plan_changed</code> — plan / interval changed.
                Informational; useful for finance + ops channels.
              </li>
              <li>
                <code>billing.invoice_paid</code> · <code>billing.invoice_failed</code>{' '}
                — payment outcome. <code>invoice_failed</code> is the urgent one;
                <code> invoice_paid</code> is a finance confirmation.
              </li>
              <li>
                <code>domain.blacklisted</code> — sending-reputation crisis alert. Fires
                only on the clean → flagged transition, so the channel doesn't get
                noisy on repeated checks.
              </li>
              <li>
                <code>domain_health.score_changed</code> — composite score crossed a
                UI band boundary downward. Useful for proactive "your authentication
                posture just regressed" alerts.
              </li>
              <li>
                <code>billing.invoice_upcoming</code> — finance-channel heads-up that
                a charge is coming in ~72h.
              </li>
              <li>
                <code>warmup_inbox.warmed_up</code> · <code>.reputation_dropped</code>{' '}
                — the milestone + regression alerts on warming mailboxes.{' '}
                <code>.daily_report</code> stays webhook-only by design — too high-
                volume for a Slack channel on tenants with many mailboxes.
              </li>
              <li>
                <code>quota.threshold_crossed</code> — "you're at 80% of monthly
                allowance on this feature". Ideal ops-channel ping.
              </li>
            </ul>
            <p className="mt-2">
              The vocabulary is intentionally the same as webhooks — every event listed here
              is also a webhook event you can subscribe an HTTP endpoint to. Pick whichever
              delivery surface (or both) fits the consuming workflow.
            </p>
            <p className="mt-2 text-[12.5px] text-faint">
              Not every webhook event is also an alert event. <code>email.duplicate_detected</code> fires
              once per alias-fold — useful for an automation script, too noisy for a Slack
              channel, so it stays webhook-only.
            </p>
          </Section>

          <Section id="errors" title="Errors & limits">
            <Code>{`401  Missing or invalid API key
402  Plan quota exceeded (emails/month, lists, or API keys)
404  Not found (or not owned by your tenant)
409  Record already decided
429  Rate limit exceeded — back off and retry`}</Code>
            <p>
              Errors return <code>{'{ "statusCode", "message" }'}</code>. Sync ingest is capped at
              1000 addresses/request (use bulk above for more). See the full schema at{' '}
              <a className="text-accent" href="/api/docs" target="_blank" rel="noreferrer">
                /api/docs
              </a>
              .
            </p>
          </Section>

          <div className="mt-12 rounded-2xl border border-bordersoft bg-gradient-to-b from-panel to-panel2 p-8 text-center">
            <h3 className="font-display text-xl font-bold">Ready to integrate?</h3>
            <p className="mt-2 text-sm text-muted">Create a workspace and generate an API key.</p>
            <Link to="/register" className="mt-4 inline-block">
              <Button variant="primary">Get started</Button>
            </Link>
          </div>
        </div>
      </div>
      <PublicFooter />
    </div>
    </>
  );
}

// Default export required by React Router framework-mode route modules.
export default DocsPage;
